import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import DOMPurify from "dompurify";
import { format } from "date-fns";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { get } from "../../../redux/ApiHelper";
import { BASE_URL } from "../../../config";
import { errorToast } from "../../../utils/toast.util";
import TableComponent from "../../../components/table/TableComponent";
import { Column } from "../../../interfaces/table.interface";
import "./AgreementRecords.scss";

interface AgreementListRow {
  _id: string;
  userId: string;
  userRole: string;
  name: string;
  email?: string;
  phoneCode: string;
  phoneNumber: string | number;
  IPAddress: string;
  termsPageCount: number;
  createdAt?: string;
  updatedAt?: string;
}

type AgreementListResponse =
  | AgreementListRow[]
  | {
      data: AgreementListRow[];
      total: number;
      page: number; // 1-based (backend)
      limit: number;
    };

interface AgreementSection {
  pageKey: string;
  title: string;
  html: string;
  error?: string;
}

interface AgreementBundle {
  recordId: string;
  userId: string;
  userRole: string;
  name: string;
  phoneCode: string;
  phoneNumber: string | number;
  phoneDisplay: string;
  IPAddress: string;
  updatedAt?: string;
  createdAt?: string;
  sections: AgreementSection[];
}

const themePaper = {
  bgcolor: "#171717",
  color: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(252, 211, 7, 0.35)",
};

function formatWhen(iso?: string) {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "dd MMM yyyy, HH:mm");
  } catch {
    return iso;
  }
}

function phoneCell(row: AgreementListRow) {
  const p = [row.phoneCode, row.phoneNumber].filter(Boolean).join(" ");
  return p || "—";
}

const AgreementRecords: React.FC = () => {
  const [rows, setRows] = useState<AgreementListRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [bundle, setBundle] = useState<AgreementBundle | null>(null);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const [page, setPage] = useState(0); // 0-based UI
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadList = useCallback(() => {
    setListLoading(true);
    const params: Record<string, any> = {
      page: page + 1,
      limit: rowsPerPage,
    };
    if (debouncedSearch) params.q = debouncedSearch;

    get("/admin/agreement-records", params)
      .then((resp: AgreementListResponse) => {
        if (Array.isArray(resp)) {
          setRows(resp);
          setTotal(resp.length);
          return;
        }
        setRows(Array.isArray(resp.data) ? resp.data : []);
        setTotal(Number(resp.total) || 0);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      })
      .finally(() => setListLoading(false));
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openPreview = async (id: string) => {
    setPreviewOpen(true);
    setBundle(null);
    setBundleLoading(true);
    try {
      const b = await get(`/admin/agreement-records/${id}/bundle`);
      setBundle(b as AgreementBundle);
    } catch {
      setBundle(null);
    } finally {
      setBundleLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setBundle(null);
  };

  const downloadPdf = async (recordId: string, existing?: AgreementBundle | null) => {
    setPdfBusyId(recordId);
    try {
      const token = localStorage.getItem("jwt");
      const res = await fetch(
        `${BASE_URL}/admin/agreement-records/${recordId}/pdf`,
        {
          method: "GET",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      if (!res.ok) {
        let msg = "Failed to download PDF";
        try {
          const j = await res.json();
          if (j?.message) msg = j.message;
        } catch {
          // ignore
        }
        throw new Error(msg);
      }

      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition");
      const m = cd && /filename="([^"]+)"/.exec(cd);
      const filename =
        m?.[1] ??
        `agreement-${existing?.recordId === recordId ? existing.userId : recordId}-${format(
          new Date(),
          "yyyy-MM-dd-HHmm"
        )}.pdf`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      errorToast(e instanceof Error ? e.message : "Failed to download PDF");
    } finally {
      setPdfBusyId(null);
    }
  };

  const columns: Column[] = [
    { select: "name", header: "Name", align: "left" },
    {
      select: "email",
      header: "Email",
      align: "left",
      render: (v) => (v ? String(v) : "—"),
    },
    {
      select: "phone",
      header: "Phone",
      align: "left",
      render: (_v, row) => phoneCell(row as AgreementListRow),
    },
    { select: "userRole", header: "Role", align: "center" },
    { select: "IPAddress", header: "IP address", align: "center" },
    { select: "termsPageCount", header: "Terms sections", align: "center" },
    {
      select: "updatedAt",
      header: "Last updated",
      align: "center",
      render: (v) => formatWhen(v as string),
    },
    {
      select: "actions",
      header: "Actions",
      align: "center",
      render: (_v, row) => {
        const r = row as AgreementListRow;
        const busy = pdfBusyId === r._id;
        return (
          <Box
            sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => openPreview(r._id)}
              sx={{
                color: "#fcd307",
                borderColor: "rgba(252, 211, 7, 0.6)",
                "&:hover": { borderColor: "#fcd307" },
              }}
            >
              Preview
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={() => downloadPdf(r._id, null)}
              sx={{
                bgcolor: "#fcd307",
                color: "#171717",
                "&:hover": { bgcolor: "#e6be06" },
              }}
            >
              {busy ? "…" : "Download PDF"}
            </Button>
          </Box>
        );
      },
    },
  ];

  return (
    <Box className="agreement-records-page" sx={{ p: 2 }}>
      <Paper elevation={0} sx={{ p: 2, mb: 2, ...themePaper }}>
        <Typography
          variant="h5"
          sx={{ color: "#fcd307", fontWeight: 700, mb: 0.5 }}
        >
          Agreement records
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)" }}>
          Users who synced terms acceptance (IP + linked page versions). Preview HTML
          or download a PDF.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, ...themePaper }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <TextField
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            size="small"
            placeholder="Search by name / phone / email"
            sx={{
              minWidth: { xs: "100%", sm: 380 },
              "& .MuiInputBase-root": {
                bgcolor: "#0f0f0f",
                color: "rgba(255,255,255,0.92)",
                borderRadius: 1,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(252, 211, 7, 0.35)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(252, 211, 7, 0.6)",
              },
              "& .MuiSvgIcon-root": { color: "rgba(252, 211, 7, 0.9)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearch("");
                      setPage(0);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        {listLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "#fcd307" }} />
          </Box>
        ) : (
          <TableComponent
            columns={columns}
            data={rows}
            serverSidePagination={{
              page,
              rowsPerPage,
              count: total,
              rowsPerPageOptions: [15, 25, 40, 100],
              onPageChange: (p) => setPage(p),
              onRowsPerPageChange: (n) => {
                setRowsPerPage(n);
                setPage(0);
              },
            }}
          />
        )}
      </Paper>

      <Dialog
        open={previewOpen}
        onClose={closePreview}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            maxHeight: "92vh",
            bgcolor: "#f5f5f5",
            backgroundImage: "none",
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#fff",
            color: "#1a1a1a",
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: "1.1rem",
            fontWeight: 600,
            borderBottom: "1px solid #e0e0e0",
            py: 1.75,
          }}
        >
          Agreement preview
        </DialogTitle>
        <DialogContent
          sx={{
            mt: 0,
            pt: 2,
            px: { xs: 2, sm: 3 },
            pb: 2,
            bgcolor: "#f5f5f5",
          }}
        >
          {bundleLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 6,
                bgcolor: "#fff",
                borderRadius: 1,
                border: "1px solid #e8e8e8",
              }}
            >
              <CircularProgress size={36} sx={{ color: "#424242" }} />
            </Box>
          )}
          {!bundleLoading && !bundle && (
            <Typography color="error" sx={{ bgcolor: "#fff", p: 2, borderRadius: 1 }}>
              Could not load agreement.
            </Typography>
          )}
          {bundle && (
            <Box
              className="agreement-preview-document"
              sx={{
                bgcolor: "#fff",
                color: "#1a1a1a",
                border: "1px solid #ddd",
                borderRadius: "2px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                px: { xs: 2.5, sm: 4 },
                py: { xs: 3, sm: 4 },
                minHeight: 200,
                fontFamily: '"Georgia", "Times New Roman", Times, serif',
              }}
            >
              <Box
                sx={{
                  mb: 3,
                  pb: 3,
                  borderBottom: "1px solid #ccc",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "6px",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#666",
                    mb: 0.25,
                  }}
                >
                  Record details
                </Typography>
                <Typography sx={{ fontSize: "8px", fontWeight: 700, color: "#000", mb: 1 }}>
                  {bundle.name}
                </Typography>
                <Box sx={{ display: "grid", gap: 0.5, maxWidth: 420 }}>
                  {[
                    ["Phone", bundle.phoneDisplay],
                    ["IP address", bundle.IPAddress],
                    ["Last updated", formatWhen(bundle.updatedAt)],
                  ].map(([label, value]) => (
                    <Box key={String(label)}>
                      <Typography
                        component="div"
                        sx={{
                          fontSize: "6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          color: "#666",
                          fontFamily: "system-ui, sans-serif",
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        component="div"
                        sx={{
                          fontSize: "8px",
                          color: "#111",
                          fontFamily: "system-ui, sans-serif",
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {bundle.sections.map((sec, i) => (
                <Box
                  key={`${sec.pageKey}-${i}`}
                  sx={{ mb: i < bundle.sections.length - 1 ? 1.5 : 0 }}
                >
                  <Typography
                    component="h2"
                    sx={{
                      fontSize: "8px",
                      fontWeight: 700,
                      color: "#000",
                      mb: 0.5,
                      pb: 0.25,
                      borderBottom: "1px solid #e5e5e5",
                    }}
                  >
                    {sec.title}
                  </Typography>
                  {sec.error ? (
                    <Typography
                      sx={{ color: "#b71c1c", fontFamily: "system-ui, sans-serif", fontSize: "8px" }}
                    >
                      {sec.error}
                    </Typography>
                  ) : (
                    <Box
                      className="agreement-preview-html"
                      sx={{
                        color: "#1a1a1a",
                        fontSize: "8px",
                        lineHeight: 1.4,
                        fontFamily: '"Georgia", "Times New Roman", Times, serif',
                        // Terms are pasted from Google Docs with inline 12pt sizes and wide margins.
                        "& *": {
                          fontSize: "inherit !important",
                          lineHeight: "inherit !important",
                        },
                        "& p, & h1, & h2, & h3, & h4, & h5, & h6": {
                          marginTop: "0 !important",
                          marginBottom: "3px !important",
                        },
                        "& p:empty": { display: "none" },
                        "& ul, & ol": { paddingLeft: "1rem", margin: "0 0 3px 0" },
                        "& hr": { margin: "4px 0" },
                        "& a": {
                          color: "#0d47a1",
                          textDecoration: "underline",
                        },
                        "& h1, & h2, & h3, & h4, & h5, & h6": {
                          fontFamily: "system-ui, sans-serif",
                          fontWeight: 700,
                          color: "#000",
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(sec.html || "<p>(empty)</p>"),
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: "#fff",
            borderTop: "1px solid #e0e0e0",
            px: 2,
            py: 1.5,
          }}
        >
          <Button onClick={closePreview} sx={{ color: "#555" }}>
            Close
          </Button>
          {bundle && (
            <Button
              variant="contained"
              disabled={!!pdfBusyId}
              onClick={() => downloadPdf(bundle.recordId, bundle)}
              sx={{
                bgcolor: "#212121",
                color: "#fff",
                "&:hover": { bgcolor: "#000" },
                "&.Mui-disabled": { bgcolor: "#bdbdbd", color: "#fff" },
              }}
            >
              {pdfBusyId ? "Generating…" : "Download PDF"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgreementRecords;
